import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import mineCraftApi from 'src/api/mineCraftApi';
import minecraftUrls from "src/configs/mine_craft/minecraftUrls";
import { AssetInterface } from 'src/store/mine_craft/assetsSlice';

const processStrategy = (value: any) => {
  return {
    ...value,
    image_url: minecraftUrls.remoteUrl(value.image_url),
  };
}

export const assetsEntity = new schema.Entity(
  'assets',
  {},
  {
    processStrategy
  }
);

export const assetEntity = new schema.Entity(
  'asset',
  {},
  {
    processStrategy
  }
);

const fetchAssetsCollection = async (url: string, params: object = {}) => {
  const response    = await mineCraftApi.get(url, { params })
  const normalized  = normalize<
    any,
    {
      assets: { [key: string]: AssetInterface };
    }
  >(response.data.assets, [assetsEntity]);

  return {
    assets: normalized.entities?.assets || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      asset: AssetInterface;
    }
  >(response.data.asset, assetEntity);
  return normalized.entities;
}

const normalizeUploadInstance = (response: any) => {
  const normalized = normalize<
    any,
    {
      asset: AssetInterface
    }
  >(response.asset, assetEntity);
  return normalized.entities;
}

export const fetchAssets = createAsyncThunk(
  'assets/fetchAssets',
  async (params: object = {}) => {
    return fetchAssetsCollection('/onesky/assets', params);
  }
);

export const fetchAsset = createAsyncThunk(
  'assets/fetchAsset',
  async (id: number | string) => {
    const response = await mineCraftApi.get(`/onesky/assets/${id}`);
    return normalizeInstance(response);
  }
);

export const uploadAsset = createAsyncThunk(
  'assets/createAsset',
  async (response: object, { rejectWithValue }) => {
    try {
      return normalizeUploadInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const createAsset = createAsyncThunk(
  'assets/createAsset',
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/assets`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateAsset = createAsyncThunk(
  'assets/updateAsset',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/assets/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyAsset = createAsyncThunk(
  'assets/destroyAsset',
  async (id: number) => {
    const response = await mineCraftApi.delete(`/onesky/assets/${id}`);
    return response.data;
  }
);

export const reorderAssets = createAsyncThunk(
  'assets/reorderAssets',
  async ({ ids }: { ids: any }, { rejectWithValue }) => {
    try {
      await mineCraftApi.patch('/onesky/assets/reorder', { ids })

      return { ids };
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);
